import { BarChart, BookOpen, Puzzle, Settings, Shield, Users, Wand2, Search, TrendingUp, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useSEO } from '@/hooks/useSEO';
import { useAdmin } from '@/hooks/useAdmin';
import { ChessAppLayout } from '@/components/ChessAppLayout';
import { AdminModuleCard } from '@/components/AdminModuleCard';
import { useState } from 'react';

const adminModules = [
  {
    title: "Security Dashboard",
    description: "Comprehensive security monitoring, threat detection, and system health checks with real-time alerts and maintenance tracking.",
    href: "/admin/security",
    icon: Shield,
    category: "Security",
    featured: true
  },
  {
    title: "Validation & Auto-Fix Wizard",
    description: "Comprehensive validation and automatic fixing for all chess components. One-click solution to ensure engine reliability and puzzle accuracy.",
    href: "/admin/validation-wizard",
    icon: Wand2,
    category: "Tools",
    featured: true
  },
  {
    title: "User Management",
    description: "Manage user accounts, roles, permissions, and view detailed user statistics and activity logs.",
    href: "/admin/users",
    icon: Users,
    category: "Management"
  },
  {
    title: "Analytics Dashboard",
    description: "Comprehensive analytics with real-time metrics, user engagement insights, and performance tracking.",
    href: "/admin/analytics",
    icon: BarChart,
    category: "Analytics"
  },
  {
    title: "User Behavior Analytics",
    description: "Detailed analysis of user behavior patterns, feature usage, and engagement metrics with comprehensive tracking.",
    href: "/admin/behavior",
    icon: Activity,
    category: "Analytics"
  },
  {
    title: "System Settings",
    description: "Configure application settings, system parameters, feature toggles, and administrative preferences.",
    href: "/admin/settings",
    icon: Settings,
    category: "Configuration"
  },
  {
    title: "Puzzle Management",
    description: "Create, edit, and manage chess puzzles with difficulty ratings, categories, and performance analytics.",
    href: "/admin/puzzles",
    icon: Puzzle,
    category: "Content"
  },
  {
    title: "Tutorial Validation",
    description: "Legacy tutorial validation system. Consider using the Auto-Fix Wizard for comprehensive validation.",
    href: "/admin/tutorial-validation",
    icon: BookOpen,
    category: "Legacy",
    deprecated: true
  }
];

export default function Admin() {
  const [searchQuery, setSearchQuery] = useState('');
  
  // SEO Configuration (noindex for security)
  useSEO({
    title: "Administrator Dashboard - ChessHatch Management",
    description: "ChessHatch administrator console for platform management, user oversight, and system analytics. Restricted access area for authorized personnel.",
    keywords: "chess admin, platform management, user management, chess analytics",
    robots: "noindex, nofollow"
  });

  const { isAdmin, loading } = useAdmin();

  const filteredModules = adminModules.filter(module =>
    module.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    module.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    module.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <ChessAppLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </div>
      </ChessAppLayout>
    );
  }

  if (!isAdmin) {
    return (
      <ChessAppLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <Shield className="h-16 w-16 text-destructive mx-auto" />
            <h1 className="text-3xl font-bold text-destructive">Access Denied</h1>
            <p className="text-muted-foreground max-w-md">
              You don't have permission to access the Super Administrator Console. 
              This area is restricted to authorized administrators only.
            </p>
          </div>
        </div>
      </ChessAppLayout>
    );
  }

  return (
    <ChessAppLayout>
      <div className="min-h-screen">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5" />
          <div className="relative container mx-auto px-6 pt-12 pb-16">
            <div className="text-center space-y-6">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div className="p-3 rounded-2xl bg-primary/10">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <div className="text-left">
                  <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                    Admin Console
                  </h1>
                  <p className="text-primary font-medium">ChessHatch Management</p>
                </div>
              </div>
              
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Comprehensive platform management dashboard with advanced analytics, 
                user oversight, and intelligent system tools
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 max-w-4xl mx-auto">
                <Card className="bg-gradient-to-br from-card to-card/50 border-primary/20">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-2xl font-bold">{filteredModules.length}</CardTitle>
                      <Activity className="h-5 w-5 text-primary" />
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-muted-foreground">Admin Modules</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-card to-card/50 border-primary/20">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-2xl font-bold">
                        {adminModules.filter(m => m.featured).length}
                      </CardTitle>
                      <TrendingUp className="h-5 w-5 text-primary" />
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-muted-foreground">Featured Tools</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-card to-card/50 border-primary/20">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-2xl font-bold">99.9%</CardTitle>
                      <Shield className="h-5 w-5 text-primary" />
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-muted-foreground">System Uptime</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-6 pb-16">
          {/* Search Bar */}
          <div className="max-w-md mx-auto mb-12">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search admin modules..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-card/50 border-primary/20 focus:border-primary/40"
              />
            </div>
          </div>

          {/* Admin Modules Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {filteredModules.map((module, index) => (
              <div 
                key={module.href}
                className="animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <AdminModuleCard {...module} />
              </div>
            ))}
          </div>

          {filteredModules.length === 0 && (
            <div className="text-center py-16">
              <Search className="h-16 w-16 text-muted-foreground/40 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No modules found</h3>
              <p className="text-muted-foreground">
                Try adjusting your search terms or browse all available modules.
              </p>
            </div>
          )}
        </div>
      </div>
    </ChessAppLayout>
  );
}